import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import MovieIcon from '@mui/icons-material/Movie';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { getAuthHeaders } from '../../utils/authUtils';
import { useEditor } from './EditorContext';
import { buildEdl, cuesToSrtWithCuts } from './editorUtils';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const VIDEO_FORMATS = [
    {
        value: 'YOUTUBE',
        label: 'YouTube / Landscape (16:9)',
        description: '1920×1080 — standard widescreen',
        resolution: '1920x1080',
        aspectRatio: '16:9',
    },
    {
        value: 'PORTRAIT',
        label: 'Portrait / Reels (9:16)',
        description: '1080×1920 — Instagram Reels, TikTok, YouTube Shorts',
        resolution: '1080x1920',
        aspectRatio: '9:16',
    },
    {
        value: 'SQUARE',
        label: 'Square (1:1)',
        description: '1080×1080 — Instagram feed',
        resolution: '1080x1080',
        aspectRatio: '1:1',
    },
    {
        value: 'TWITTER',
        label: 'Twitter / X (16:9 HD)',
        description: '1280×720 — optimised for Twitter/X',
        resolution: '1280x720',
        aspectRatio: '16:9',
    },
];

const EXPORT_TYPES = [
    {
        value: 'video_with_subs',
        label: 'Video + Burned Subtitles',
        icon: <ClosedCaptionIcon sx={{ fontSize: 18 }} />,
        description: 'Exports the edited video with subtitles permanently burned in',
    },
    {
        value: 'video_only',
        label: 'Video Only (No Subtitles)',
        icon: <MovieIcon sx={{ fontSize: 18 }} />,
        description: 'Exports the edited video without any subtitle overlay',
    },
    {
        value: 'audio_only',
        label: 'Audio Only',
        icon: <AudiotrackIcon sx={{ fontSize: 18 }} />,
        description: 'Exports just the audio track (MP3)',
    },
];

/**
 * ExportDialog — lets user choose export type and triggers backend export.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 */
export default function ExportDialog({ open, onClose }) {
    useTrackUsage('ExportDialog', 'dialog', 'videoeditor', open);
    const { state } = useEditor();
    const { videoId, videoTitle, duration, cuts, subtitlesByLanguage, audioTracks, muteVideoAudio, logo } = state;

    const [exportType, setExportType] = useState('video_with_subs');
    const [selectedLang, setSelectedLang] = useState(
        Object.keys(subtitlesByLanguage)[0] || 'English',
    );
    const [selectedFormat, setSelectedFormat] = useState('YOUTUBE');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);

    const langKeys = Object.keys(subtitlesByLanguage);
    const cutCount = cuts.length;

    const pollExportStatus = async (jobId, apiBaseUrl, headers) => {
        const maxAttempts = 120; // 10 minutes at 5s intervals
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise((r) => setTimeout(r, 5000));
            const statusRes = await fetch(`${apiBaseUrl}/api/v3/videos/export/status/${jobId}`, { headers });
            if (!statusRes.ok) continue;
            const statusData = await statusRes.json();
            if (statusData.status === 'COMPLETED') {
                return statusData.downloadUrl;
            }
            if (statusData.status === 'FAILED') {
                throw new Error(statusData.message || 'Export failed on server');
            }
        }
        throw new Error('Export timed out. Please check back later.');
    };

    const handleExport = async () => {
        setError(null);
        setLoading(true);
        try {
            const edl = buildEdl(duration, cuts);
            const adjustedSrt =
                exportType === 'video_with_subs' && subtitlesByLanguage[selectedLang]
                    ? cuesToSrtWithCuts(subtitlesByLanguage[selectedLang], cuts)
                    : null;

            const fmt = VIDEO_FORMATS.find((f) => f.value === selectedFormat);
            const formatPayload = fmt
                ? { name: fmt.value, resolution: fmt.resolution, aspectRatio: fmt.aspectRatio }
                : null;

            const payload = {
                videoId,
                edl,
                outputType: exportType,
                language: exportType === 'video_with_subs' ? selectedLang : null,
                subtitleContent: adjustedSrt,
                subtitleFormat: 'srt',
                format: formatPayload,
                audioTracks: audioTracks.map(({ id, url, name, volume, offsetSeconds }) => ({ id, url, name, volume, offsetSeconds })),
                muteVideoAudio,
                logoUrl: logo?.url || null,
                logoName: logo?.name || null,
                logoPosition: logo?.position || null,
                logoOpacity: logo?.opacity ?? null,
                logoStartTime: logo?.startTime ?? null,
                logoEndTime: logo?.endTime ?? null,
            };

            const apiBaseUrl = process.env.REACT_APP_API_URL || '';
            const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
            const response = await fetch(`${apiBaseUrl}/api/v3/videos/export`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || `Export failed: ${response.statusText}`);
            }

            const data = await response.json();
            const jobId = data.jobId;

            if (!jobId) {
                // Synchronous response with immediate download URL
                if (data.downloadUrl) window.open(data.downloadUrl, '_blank');
                setSuccess(true);
                return;
            }

            setSuccess(true); // show "export started" message while polling
            const url = await pollExportStatus(jobId, apiBaseUrl, getAuthHeaders());
            if (url) {
                setDownloadUrl(url);
                window.open(url, '_blank');
            }
        } catch (err) {
            setError(err.message || 'Export failed. Please try again.');
            setSuccess(false);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setError(null);
        setSuccess(false);
        setDownloadUrl(null);
        setSelectedFormat('YOUTUBE');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { background: '#1a1a2e', color: '#fff', border: '1px solid #2a2a4a' } }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid #2a2a3a', pb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MovieIcon sx={{ color: '#64b5f6' }} />
                    <Typography variant="h6">Export</Typography>
                    {cutCount > 0 && (
                        <Chip
                            label={`${cutCount} cut${cutCount !== 1 ? 's' : ''} applied`}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: 10 }}
                        />
                    )}
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5 }}>
                {success ? (
                    <Alert severity="success" sx={{ my: 1 }}>
                        {loading ? (
                            'Export in progress… please wait.'
                        ) : downloadUrl ? (
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Export complete!
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    href={downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ textTransform: 'none' }}
                                >
                                    Download Video
                                </Button>
                            </Box>
                        ) : (
                            'Export complete!'
                        )}
                    </Alert>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                            The following edits will be applied:
                        </Typography>

                        {cutCount === 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No cuts made yet — the original video will be exported.
                            </Alert>
                        ) : (
                            <Box sx={{ mb: 2 }}>
                                {cuts.map((cut, i) => (
                                    <Chip
                                        key={cut.id}
                                        label={`Cut ${i + 1}: ${formatTime(cut.startTime)} → ${formatTime(cut.endTime)}`}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        sx={{ mr: 0.5, mb: 0.5, fontSize: 10 }}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Audio tracks / Logo / Mute summary chips */}
                        {(audioTracks.length > 0 || logo || muteVideoAudio) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {audioTracks.map((t) => (
                                    <Chip
                                        key={t.id}
                                        icon={<AudiotrackIcon sx={{ fontSize: 13 }} />}
                                        label={`Audio: ${t.name} (vol ${Math.round(t.volume * 100)}%)`}
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{ fontSize: 10 }}
                                    />
                                ))}
                                {muteVideoAudio && (
                                    <Chip
                                        label="Video audio muted"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ fontSize: 10 }}
                                    />
                                )}
                                {logo && (
                                    <Chip
                                        icon={<BrandingWatermarkIcon sx={{ fontSize: 13 }} />}
                                        label={`Logo: ${logo.name} · ${logo.position}`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ fontSize: 10 }}
                                    />
                                )}
                            </Box>
                        )}

                        <Divider sx={{ borderColor: '#2a2a3a', my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#ccc' }}>
                            Export Type
                        </Typography>

                        <RadioGroup
                            value={exportType}
                            onChange={(e) => setExportType(e.target.value)}
                        >
                            {EXPORT_TYPES.map((opt) => (
                                <Box
                                    key={opt.value}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        p: 1.2,
                                        mb: 1,
                                        border: '1px solid',
                                        borderColor:
                                            exportType === opt.value ? '#1565c0' : '#2a2a3a',
                                        borderRadius: 1,
                                        background:
                                            exportType === opt.value
                                                ? 'rgba(21,101,192,0.1)'
                                                : 'transparent',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExportType(opt.value)}
                                >
                                    <FormControlLabel
                                        value={opt.value}
                                        control={<Radio size="small" sx={{ color: '#64b5f6' }} />}
                                        label=""
                                        sx={{ m: 0 }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3, flex: 1 }}>
                                        <span style={{ color: '#64b5f6' }}>{opt.icon}</span>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {opt.label}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                {opt.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </RadioGroup>

                        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                            <InputLabel sx={{ color: '#888' }}>Video Format</InputLabel>
                            <Select
                                value={selectedFormat}
                                label="Video Format"
                                onChange={(e) => setSelectedFormat(e.target.value)}
                                sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2a4a' } }}
                            >
                                {VIDEO_FORMATS.map((f) => (
                                    <MenuItem key={f.value} value={f.value}>
                                        <Box>
                                            <Typography variant="body2">{f.label}</Typography>
                                            <Typography variant="caption" sx={{ color: '#888' }}>{f.description}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {exportType === 'video_with_subs' && langKeys.length > 0 && (
                            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                                <InputLabel sx={{ color: '#888' }}>Subtitle Language</InputLabel>
                                <Select
                                    value={selectedLang}
                                    label="Subtitle Language"
                                    onChange={(e) => setSelectedLang(e.target.value)}
                                    sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2a4a' } }}
                                >
                                    {langKeys.map((l) => (
                                        <MenuItem key={l} value={l}>
                                            {l}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid #2a2a3a', px: 2, py: 1.5, gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: '#888' }}>
                    {success ? 'Close' : 'Cancel'}
                </Button>
                {!success && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExport}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <MovieIcon />}
                    >
                        {loading ? 'Exporting…' : 'Export'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

function formatTime(sec) {
    const m = Math.floor(sec / 60);
    const s = (sec % 60).toFixed(1);
    return m > 0 ? `${m}m${s}s` : `${s}s`;
}
